import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { InputBar, Pagination, Spinner } from '~/ui';
import styles from './TournamentLeaderboard.module.scss';
import TournamentLeaderboardTable from './TournamentLeaderboardTable/TournamentLeaderboardTable';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router-dom';

const TournamentLeaderboard = ({ gameId }: { gameId: number }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedParams = useDebounce(searchTerm, 500);
  const { tournamentId } = useParams();
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const { t } = useTranslation();

  const params = {
    game_id: gameId,
    tournament_id: tournamentId,
    sort: sort.direction,
    field: sort.field,
    page,
  };

  const {
    data: leaderboard,
    isLoading,
    isError,
  } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(`admin/game-leaderboard`, { params });
      return data;
    },
    queryKey: [
      'leaderboard',
      gameId,
      page,
      debouncedParams,
      sort,
      tournamentId,
    ],
  });

  return (
    <Board classNames={styles.board}>
      <div className={styles.searchWrapper}>
        <InputBar
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          value={searchTerm}
          placeholder={t('search_by_id_np')}
          classNameInput={styles.accommodationsTableInput}
          label={`${t('search')}:`}
          star={false}
          classNameInputWrapper={styles.accommodationsTableInputWrapper}
        />
      </div>
      <div className={styles.tableWrapper}>
        {!leaderboard && isLoading ? (
          <Spinner />
        ) : isError ? (
          <div className={styles.error}>{t('leaderboard_loading_error')}</div>
        ) : (
          <TournamentLeaderboardTable
            sort={sort}
            setSort={setSort}
            data={leaderboard?.data}
          />
        )}
      </div>
      <div>
        <Pagination
          pageCount={leaderboard?.last_page || 0}
          onPageChange={(selectedPage: { selected: number }) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};

export default TournamentLeaderboard;
